import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ShieldCheck, WarningCircle } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AcceptAdminInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [requirements, setRequirements] = useState([]);
  const [pwd, setPwd] = useState({ password: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/auth/admin-invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Invito non valido'));
    axios.get(`${API}/api/auth/password-requirements`)
      .then((r) => setRequirements(r.data.requirements || []))
      .catch(() => {});
  }, [token]);

  const accept = async () => {
    if (pwd.password !== pwd.confirm) { toast.error('Le due password non coincidono'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/auth/admin-invite/${token}/accept`, { password: pwd.password });
      await login(r.data.token, r.data.user);
      toast.success('Account amministratore attivato');
      navigate('/admin');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore attivazione account');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5FAFF] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md rounded-2xl p-8" data-testid="accept-admin-invite-page">
        <img src="/vivalink-logo-transparent.png" alt="VivaLink Italia" className="h-14 mx-auto mb-6 object-contain" />

        {error ? (
          <div className="text-center space-y-3" data-testid="invite-error">
            <WarningCircle size={40} weight="duotone" className="text-amber-600 mx-auto" />
            <p className="font-heading font-medium text-lg">Invito non utilizzabile</p>
            <p className="text-sm text-foreground/70">{error}</p>
            <Button variant="outline" className="rounded-full" onClick={() => navigate('/login')} data-testid="invite-to-login">
              Vai all'accesso
            </Button>
          </div>
        ) : !invite ? (
          <p className="text-center text-foreground/60" data-testid="invite-loading">Verifica invito…</p>
        ) : (
          <div className="space-y-5">
            <div className="text-center">
              <ShieldCheck size={36} weight="duotone" className="text-primary mx-auto mb-2" />
              <h1 className="font-heading font-semibold text-2xl">Benvenuto, {invite.name}</h1>
              <p className="text-sm text-foreground/70 mt-1">
                {invite.invited_by_name ? `${invite.invited_by_name} ti ha invitato` : 'Sei stato invitato'} come
                amministratore con livello <strong>{invite.admin_level_label}</strong>.
              </p>
              <p className="text-xs text-foreground/55 mt-2">Imposta la tua password per attivare l'accesso.</p>
            </div>

            <div>
              <Label>Email</Label>
              <Input value={invite.email} disabled data-testid="invite-email" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={pwd.password} onChange={(e) => setPwd({ ...pwd, password: e.target.value })} data-testid="invite-password" />
            </div>
            <div>
              <Label>Conferma password</Label>
              <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="invite-password-confirm" />
            </div>
            {requirements.length > 0 && (
              <ul className="text-xs text-foreground/60 space-y-0.5">
                {requirements.map((r, i) => <li key={i}>• {r}</li>)}
              </ul>
            )}
            <Button onClick={accept} disabled={busy || !pwd.password} className="rounded-full w-full" data-testid="invite-accept-btn">
              Attiva il mio account
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AcceptAdminInvitePage;
